import {
	useDeleteManyManufacturersMutation,
	useDeleteManufacturerMutation,
	useGetAllManufacturersQuery,
	useGetManufacturerStatsQuery,
} from '@codezo/graphql';
import { ListFooter, PagingModeToggle, usePagedList, type RowEstimate } from '@codezo/lists';
import { toast } from 'sonner';
import { AddManufacturerModal } from './AddManufacturerModal';
import { EditManufacturerModal } from './EditManufacturerModal';
import { VetterSelector } from '../../components/selectors/VetterSelector';
import { Factory, MoreHorizontal, Pencil, Plus, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSetPageMeta } from '../../hooks/useSetPageMeta';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { SearchInput } from '../../components/ui/search-input';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '../../components/ui/table';
import { Tabs } from '../../components/ui/tabs';
import { Checkbox } from '../../components/ui/checkbox';
import { SelectionBar } from '../../components/ui/selection-bar';
import { StatusBadge } from '../../components/ui/status-badge';
import { InitialsAvatar } from '../../components/ui/initials-avatar';
import { ViewToggle } from '../../components/ui/view-toggle';
import { EntityCard } from '../../components/ui/entity-card';
import { formatDate } from '../../lib/utils';
import { useListParams } from '../../hooks/useListParams';
import { useQueryParam } from '../../hooks/useQueryParam';
import { CardSkeletons, TableSkeleton } from '../../components/ui/list-skeletons';
import { EmptyState } from '../../components/ui/empty-state';
import { SortableTableHead } from '../../components/ui/sortable-table-head';

/**
 * One row of manufacturers as laid out at the current viewport, for sizing scroll-mode batches.
 * Mirrors the card grid below (1/2/3 columns at sm/lg, 14px gap) of EntityCards: the ~142px
 * shell (padding, 44px avatar header, metrics strip) plus a description that wraps to about
 * two lines. A table row is the 32px logo plus cell padding and border.
 */
function manufacturerRows(view: string): RowEstimate {
	if (view !== 'card') return { rowHeight: 53 };
	const vw = window.innerWidth;
	const columns = vw >= 1024 ? 3 : vw >= 640 ? 2 : 1;
	return { columns, rowHeight: 142 + 50 + 14 };
}

/* ════════════════════════ MANUFACTURERS PAGE ════════════════════════ */

export function ManufacturersPage() {
	const navigate = useNavigate();
	useSetPageMeta('Manufacturers', [{ label: 'Manufacturers' }]);
	const {
		search,
		setSearch,
		debouncedSearch,
		page,
		setPage,
		view,
		setView,
		sortField,
		sortDirection,
		sortDir,
		toggleSort,
		limit,
		mode,
		setMode,
		request,
		loadMore,
		reload,
	} = useListParams({
		defaultSort: 'name',
		defaultDir: 'asc',
		defaultView: 'card',
		scrollRows: manufacturerRows,
	});
	const [tab, setTab] = useQueryParam('tab', 'all');
	const [vetter, setVetterParam] = useQueryParam('vetter', 'all');
	const setVetter = (value: string) => {
		setVetterParam(value);
		setPage(1);
	};
	const [addModalOpen, setAddModalOpen] = useState(false);
	const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
	const [editTarget, setEditTarget] = useState<{
		id: string;
		name: string;
		country?: string | null;
		description?: string | null;
		website?: string | null;
		isActive: boolean;
		vetterEntityId?: string | null;
	} | null>(null);

	const { data, loading, refetch } = useGetAllManufacturersQuery({
		variables: {
			input: {
				pagination: request,
				order: { field: sortField, direction: sortDirection },
				filter: {
					...(debouncedSearch ? { name: debouncedSearch } : {}),
					...(tab === 'active'
						? { isActive: true }
						: tab === 'inactive'
							? { isActive: false }
							: {}),
					...(vetter === 'unassigned'
						? { unassignedToVetterEntity: true }
						: vetter !== 'all'
							? { vetterEntityId: vetter }
							: {}),
				},
			},
		},
		fetchPolicy: 'no-cache',
	});

	const list = usePagedList(
		{ mode, request, limit, setPage, loadMore },
		data?.getAllManufacturer,
		loading
	);
	const manufacturers = list.items;

	// Dataset-wide KPI stats (independent of the active tab/search/page).
	const { data: statsData, refetch: refetchStats } = useGetManufacturerStatsQuery({
		fetchPolicy: 'no-cache',
	});
	const stats = statsData?.getManufacturerStats;

	const refresh = () => {
		reload(refetch);
		refetchStats();
	};

	const [deleteManufacturer, { loading: deleting }] = useDeleteManufacturerMutation();
	const handleDelete = async () => {
		if (!deleteTarget) return;
		try {
			await deleteManufacturer({ variables: { input: { id: deleteTarget.id } } });
			toast.success('Manufacturer deleted');
			setDeleteTarget(null);
			refresh();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to delete manufacturer');
		}
	};

	/* ── Selection state ── */
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
	const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

	const mfrIds = useMemo(() => manufacturers.map((m) => m.id), [manufacturers]);
	const allSelected = mfrIds.length > 0 && mfrIds.every((id) => selectedIds.has(id));
	const someSelected = mfrIds.some((id) => selectedIds.has(id));

	const toggleOne = (id: string) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	const toggleAll = () => {
		setSelectedIds(allSelected ? new Set() : new Set(mfrIds));
	};

	// Clear selection when the rows on screen are replaced. Scrolling keeps earlier rows on
	// screen, so in scroll mode loading more does not clear it.
	const shownPage = mode === 'pages' ? page : 0;
	useEffect(() => {
		setSelectedIds(new Set());
	}, [shownPage, mode, debouncedSearch, tab, vetter]);

	/* ── Bulk delete ── */
	const [deleteManyManufacturer, { loading: bulkDeleting }] =
		useDeleteManyManufacturersMutation();
	const handleBulkDelete = async () => {
		try {
			await deleteManyManufacturer({
				variables: { input: { ids: Array.from(selectedIds) } },
			});
			toast.success(
				`${selectedIds.size} manufacturer${selectedIds.size > 1 ? 's' : ''} deleted`
			);
			setSelectedIds(new Set());
			setBulkDeleteOpen(false);
			refresh();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to delete manufacturers');
		}
	};

	return (
		<div className="mx-auto max-w-[var(--content-max)] px-4 pt-6 pb-20 sm:px-6 md:px-8 md:pt-8">
			{/* ── Header ── */}
			<div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
				<div>
					<h1 className="mb-2 text-2xl font-medium leading-tight -tracking-[0.02em] text-text-1 sm:text-3xl">
						Manufacturers
					</h1>
					<p className="text-base text-text-3">
						Vetted production partners and candidates across your supply chain.
					</p>
				</div>
				<div className="flex gap-2">
					<Button variant="outline" size="sm" onClick={() => setAddModalOpen(true)}>
						<Plus size={14} data-icon="inline-start" />
						Add manufacturer
					</Button>
				</div>
			</div>

			{/* ── KPI strip ── */}
			<div className="mb-7 grid grid-cols-2 divide-x divide-y divide-line-1 overflow-hidden rounded-[var(--r-3)] border border-line-1 bg-surface-sunken lg:grid-cols-4">
				{[
					{ label: 'Total', value: stats?.total ?? 0, sub: 'All manufacturers' },
					{ label: 'Active', value: stats?.active ?? 0, sub: 'All manufacturers' },
					{ label: 'Inactive', value: stats?.inactive ?? 0, sub: 'All manufacturers' },
					{
						label: 'With vetter',
						value: stats?.withVetter ?? 0,
						sub: 'All manufacturers',
					},
				].map((k) => (
					<div key={k.label} className="px-5 py-4">
						<div className="mb-1.5 text-2xs font-medium uppercase tracking-[0.08em] text-text-4">
							{k.label}
						</div>
						<div className="mb-1 font-mono text-2xl font-medium text-text-1">
							{k.value}
						</div>
						<div className="text-xs text-text-3">{k.sub}</div>
					</div>
				))}
			</div>

			{/* ── Tabs + filters row ── */}
			<div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between lg:gap-4">
				<Tabs
					value={tab}
					onChange={(v) => {
						setTab(v);
						setPage(1);
					}}
					items={[
						{ value: 'all', label: 'All' },
						{ value: 'active', label: 'Active' },
						{ value: 'inactive', label: 'Inactive' },
					]}
				/>
				<div className="flex items-center gap-2">
					<SearchInput
						value={search}
						onChange={setSearch}
						placeholder="Search manufacturers…"
						className="flex-1 lg:flex-none lg:w-[240px]"
					/>
					<VetterSelector
						value={vetter}
						onChange={setVetter}
						placeholder="All vetters"
						extraOptions={[
							{ value: 'all', label: 'All vetters' },
							{ value: 'unassigned', label: 'Unassigned' },
						]}
						variant="toolbar"
						className="w-[180px] shrink-0"
					/>
					<ViewToggle value={view} onChange={setView} />
					<PagingModeToggle value={mode} onChange={setMode} />
				</div>
			</div>

			{/* ── Content: Card view or List view ── */}
			{list.loading ? (
				view === 'card' ? (
					<CardSkeletons />
				) : (
					<TableSkeleton />
				)
			) : manufacturers.length === 0 ? (
				<EmptyState
					icon={Factory}
					title="No manufacturers found"
					description={
						search
							? 'Try a different search term.'
							: tab === 'active'
								? 'No active manufacturers.'
								: tab === 'inactive'
									? 'No inactive manufacturers.'
									: vetter === 'unassigned'
										? 'No unassigned manufacturers.'
										: vetter !== 'all'
											? 'No manufacturers assigned to this vetter.'
											: 'No manufacturers have been added yet.'
					}
				/>
			) : view === 'card' ? (
				/* ── Card grid (3 columns) ── */
				<div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:grid-cols-3">
					{manufacturers.map((m) => (
						<EntityCard
							key={m.id}
							name={m.name}
							avatar={m.logoUrl}
							subtitle={m.country || 'Unknown region'}
							badge={<StatusBadge active={m.isActive} />}
							actions={
								<DropdownMenu>
									<DropdownMenuTrigger
										onClick={(e) => e.stopPropagation()}
										className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-text-3 transition-colors hover:bg-surface-hover hover:text-text-1"
									>
										<MoreHorizontal size={14} />
									</DropdownMenuTrigger>
									<DropdownMenuContent align="end">
										<DropdownMenuItem
											onClick={(e) => {
												e.stopPropagation();
												setEditTarget({
													id: m.id,
													name: m.name,
													country: m.country,
													description: m.description,
													website: m.website,
													isActive: m.isActive,
													vetterEntityId: m.vetterEntity?.id,
												});
											}}
										>
											<Pencil size={14} /> Edit
										</DropdownMenuItem>
										<DropdownMenuItem
											variant="destructive"
											onClick={(e) => {
												e.stopPropagation();
												setDeleteTarget({ id: m.id, name: m.name });
											}}
										>
											<Trash2 size={14} /> Delete
										</DropdownMenuItem>
									</DropdownMenuContent>
								</DropdownMenu>
							}
							metrics={[
								{ label: 'Vetter', value: m.vetterEntity?.name || '—' },
								{
									label: 'Website',
									value: m.website
										? m.website.replace(/^https?:\/\//, '').replace(/\/$/, '')
										: '—',
								},
								{
									label: 'Created',
									value: m.createdAt ? formatDate(m.createdAt) : '—',
								},
							]}
							metricColumns={3}
							onClick={() => navigate(`/suppliers/${m.id}`)}
						>
							<div className="mb-3.5 text-sm text-text-3">{m.description || '—'}</div>
						</EntityCard>
					))}
				</div>
			) : (
				/* ── List/table view ── */
				<Card className="overflow-hidden border-line-1 bg-surface gap-0 py-0">
					<div className="overflow-x-auto">
						<Table className="min-w-[700px]">
							<TableHeader>
								<TableRow className="border-line-1 hover:bg-transparent">
									<TableHead className="w-10">
										<Checkbox
											checked={allSelected}
											indeterminate={someSelected && !allSelected}
											onCheckedChange={toggleAll}
										/>
									</TableHead>
									<SortableTableHead
										sortKey="name"
										activeKey={sortField}
										direction={sortDir}
										onSort={toggleSort}
										className="w-[25%] text-xs font-medium uppercase tracking-[0.06em] text-text-4"
									>
										Manufacturer
									</SortableTableHead>
									<SortableTableHead
										sortKey="description"
										activeKey={sortField}
										direction={sortDir}
										onSort={toggleSort}
										className="w-[25%] text-xs font-medium uppercase tracking-[0.06em] text-text-4"
									>
										Description
									</SortableTableHead>
									<SortableTableHead
										sortKey="country"
										activeKey={sortField}
										direction={sortDir}
										onSort={toggleSort}
										className="w-[15%] text-xs font-medium uppercase tracking-[0.06em] text-text-4"
									>
										Country
									</SortableTableHead>
									<SortableTableHead
										sortKey="website"
										activeKey={sortField}
										direction={sortDir}
										onSort={toggleSort}
										className="w-[15%] text-xs font-medium uppercase tracking-[0.06em] text-text-4"
									>
										Website
									</SortableTableHead>
									<SortableTableHead
										sortKey="isActive"
										activeKey={sortField}
										direction={sortDir}
										onSort={toggleSort}
										className="w-[90px] text-center text-xs font-medium uppercase tracking-[0.06em] text-text-4"
									>
										Status
									</SortableTableHead>
									<SortableTableHead
										sortKey="createdAt"
										activeKey={sortField}
										direction={sortDir}
										onSort={toggleSort}
										className="text-xs font-medium uppercase tracking-[0.06em] text-text-4"
									>
										Created
									</SortableTableHead>
									<TableHead className="w-10" />
								</TableRow>
							</TableHeader>
							<TableBody>
								{manufacturers.map((m) => (
									<TableRow
										key={m.id}
										data-state={selectedIds.has(m.id) ? 'selected' : undefined}
										className="cursor-pointer border-line-1 transition-colors duration-[180ms] hover:bg-surface-hover last:border-b-0"
										onClick={() => navigate(`/suppliers/${m.id}`)}
									>
										<TableCell>
											<div onClick={(e) => e.stopPropagation()}>
												<Checkbox
													checked={selectedIds.has(m.id)}
													onCheckedChange={() => toggleOne(m.id)}
												/>
											</div>
										</TableCell>
										<TableCell>
											<div className="flex items-center gap-3">
												<InitialsAvatar
													name={m.name}
													src={m.logoUrl}
													size={32}
													shape="rounded"
												/>
												<span className="text-base font-medium text-text-1">
													{m.name}
												</span>
											</div>
										</TableCell>
										<TableCell className="max-w-[240px] truncate text-base text-text-2">
											{m.description || '—'}
										</TableCell>
										<TableCell className="text-base text-text-2">
											{m.country || '—'}
										</TableCell>
										<TableCell className="max-w-[160px] truncate text-base text-text-2">
											{m.website
												? m.website
														.replace(/^https?:\/\//, '')
														.replace(/\/$/, '')
												: '—'}
										</TableCell>
										<TableCell className="text-center">
											<StatusBadge active={m.isActive} />
										</TableCell>
										<TableCell className="text-base text-text-3">
											{m.createdAt ? formatDate(m.createdAt) : '—'}
										</TableCell>
										<TableCell>
											<div className="flex justify-end">
												<DropdownMenu>
													<DropdownMenuTrigger
														onClick={(e) => e.stopPropagation()}
														className="flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-text-3 transition-colors hover:bg-surface-hover hover:text-text-1"
													>
														<MoreHorizontal size={14} />
													</DropdownMenuTrigger>
													<DropdownMenuContent align="end">
														<DropdownMenuItem
															onClick={(e) => {
																e.stopPropagation();
																setEditTarget({
																	id: m.id,
																	name: m.name,
																	country: m.country,
																	description: m.description,
																	website: m.website,
																	isActive: m.isActive,
																	vetterEntityId:
																		m.vetterEntity?.id,
																});
															}}
														>
															<Pencil size={14} /> Edit
														</DropdownMenuItem>
														<DropdownMenuItem
															variant="destructive"
															onClick={(e) => {
																e.stopPropagation();
																setDeleteTarget({
																	id: m.id,
																	name: m.name,
																});
															}}
														>
															<Trash2 size={14} /> Delete
														</DropdownMenuItem>
													</DropdownMenuContent>
												</DropdownMenu>
											</div>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>
				</Card>
			)}

			<SelectionBar
				count={selectedIds.size}
				actions={[
					{ label: 'Delete', icon: Trash2, onClick: () => setBulkDeleteOpen(true) },
				]}
				onClear={() => setSelectedIds(new Set())}
			/>

			<ListFooter list={list} />

			<AddManufacturerModal
				open={addModalOpen}
				onOpenChange={setAddModalOpen}
				onSuccess={() => refresh()}
			/>

			<EditManufacturerModal
				open={!!editTarget}
				onOpenChange={(open) => {
					if (!open) setEditTarget(null);
				}}
				manufacturer={editTarget}
				onSuccess={() => refresh()}
			/>

			<ConfirmDialog
				open={!!deleteTarget}
				onOpenChange={(open) => {
					if (!open) setDeleteTarget(null);
				}}
				title="Delete manufacturer"
				description={`Are you sure you want to delete "${deleteTarget?.name}"? Quotes or offers that reference this manufacturer may be affected. This action cannot be undone.`}
				onConfirm={handleDelete}
				loading={deleting}
			/>

			<ConfirmDialog
				open={bulkDeleteOpen}
				onOpenChange={setBulkDeleteOpen}
				title="Delete manufacturers"
				description={`Are you sure you want to delete ${selectedIds.size} manufacturer${selectedIds.size > 1 ? 's' : ''}? Quotes or offers that reference them may be affected. This action cannot be undone.`}
				onConfirm={handleBulkDelete}
				loading={bulkDeleting}
			/>
		</div>
	);
}
